import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useDropzone } from 'react-dropzone';
import { Storage } from 'aws-amplify';
import IconButton from '@material-ui/core/IconButton';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme) => ({
  imgContainer: {
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[300],
    // backgroundColor: '#000',
  },
  dropzone: {
    border: '1px dotted black',
    paddingLeft: 16,
    marginTop: 16,
    cursor: 'pointer',
    borderRadius: 5,
    textAlign: 'center',
    minHeight: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginTop: theme.spacing(1),
  },
}));

// 圖片裁切函數
const cropImageToFit = (file, targetWidth = 700, targetHeight = 480) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;

      // 計算裁切比例
      const targetRatio = targetWidth / targetHeight;
      const imgRatio = imgWidth / imgHeight;

      let cropWidth;
      let cropHeight;
      let cropX;
      let cropY;

      if (imgRatio > targetRatio) {
        // 圖片太寬，需要裁切左右
        cropHeight = imgHeight;
        cropWidth = imgHeight * targetRatio;
        cropX = (imgWidth - cropWidth) / 2;
        cropY = 0;
      } else {
        // 圖片太高，需要裁切上下
        cropWidth = imgWidth;
        cropHeight = imgWidth / targetRatio;
        cropX = 0;
        cropY = (imgHeight - cropHeight) / 2;
      }

      // 設置 canvas 尺寸
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // 裁切並縮放圖片
      ctx.drawImage(
        img,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, targetWidth, targetHeight,
      );

      // 轉換為 Blob
      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });
        resolve(croppedFile);
      }, file.type, 0.9);
    };

    img.src = URL.createObjectURL(file);
  });
};

export default function ImageGalleryUploader({
  s3Prefix,
  s3Keys: inS3Keys = [],
  onUpdateS3Keys,
  maxFileSize = 2 * 1024 * 1024, // 2MB 預設值
  cropSettings = null,
  showFileSizeLimit = false,
}) {
  const classes = useStyles();
  const [s3Keys, setS3Keys] = useState([...inS3Keys]);
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');

  const {
    getRootProps,
    getInputProps,
  } = useDropzone({
    async onDrop(newFiles) {
      setIsLoading(true);
      setError('');
      global.logger.debug('onDrop', newFiles);

      if (newFiles.length === 0) return;

      try {
        await Promise.all(newFiles.map(async (file) => {
          // 檢查文件大小
          if (file.size > maxFileSize) {
            throw new Error(`文件大小不能超過 ${Math.round(maxFileSize / 1024 / 1024)}MB`);
          }

          let processedFile = file;

          // 如果需要裁切圖片
          if (cropSettings) {
            processedFile = await cropImageToFit(file, cropSettings.width, cropSettings.height);
          }

          const s3Key = `${s3Prefix}/${processedFile.name}`;
          await Storage.put(s3Key, processedFile, {
            contentType: processedFile.type,
          });

          s3Keys.push(s3Key);
          files.push({
            s3Key,
            url: URL.createObjectURL(processedFile),
          });
          setS3Keys([...s3Keys]);
        }));

        setFiles([...files]);
        onUpdateS3Keys(s3Keys);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    },
    accept: 'image/*',
  });

  const onRemoveFile = async (s3Key) => {
    setIsLoading(true);
    try {
      await Storage.remove(s3Key);
    } catch (error) {
      // 只有Admins有權限刪除圖片，其他角色只有移除DB的資料
      console.error('onRemoveFile', error);
    }
    const fileIndex = files.findIndex((file) => file.s3Key === s3Key);
    files.splice(fileIndex, 1);

    const s3KeyIndex = s3Keys.findIndex((key) => key === s3Key);
    s3Keys.splice(s3KeyIndex, 1);

    setFiles([...files]);
    setS3Keys([...s3Keys]);
    onUpdateS3Keys(s3Keys);
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      const files = await Promise.all(s3Keys.map(async (s3Key) => {
        return {
          s3Key,
          url: await Storage.get(s3Key),
        };
      }));

      setFiles(files);
    })();
  }, [s3Keys]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <section className={classes.dropzone}>
          <div {...getRootProps()}>
            <input {...getInputProps()} disabled={isLoading} />
            {isLoading && <CircularProgress color="primary" size={24} />}
            {!isLoading && (
              <div>
                <p>點此或拖曳上傳圖片</p>
                {showFileSizeLimit && (
                  <Typography variant="body2" color="textSecondary">
                    最大檔案大小：{Math.round(maxFileSize / 1024 / 1024)}MB
                  </Typography>
                )}
                {cropSettings && (
                  <Typography variant="body2" color="textSecondary">
                    圖片將自動裁切為 {cropSettings.width} x {cropSettings.height} 像素
                  </Typography>
                )}
              </div>
            )}
          </div>
        </section>
        {error && (
          <Alert severity="error" style={{ marginTop: 8 }}>
            {error}
          </Alert>
        )}
      </Grid>
      {files.map(({ s3Key, url }, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={index} className={classes.imgContainer}>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => onRemoveFile(s3Key)}
            disabled={isLoading}
          >
            <HighlightOffIcon />
          </IconButton>
          <img id="url" src={url} width="100%" onError={(e) => e.target.src = '/images/fallback.webp'} />
        </Grid>
      ))}
    </Grid>
  );
}

ImageGalleryUploader.propTypes = {
  s3Prefix: PropTypes.string,
  s3Keys: PropTypes.array,
  onUpdateS3Keys: PropTypes.func,
  maxFileSize: PropTypes.number,
  cropSettings: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  showFileSizeLimit: PropTypes.bool,
};
